import React from "react";
import { StudentWithUserInfo } from "../../types/student";

interface PaymentReceiptProps {
  student: StudentWithUserInfo;
  payment: {
    amount: number;
    paymentMethod: string;
    referenceNumber?: string;
    paymentDate: string;
    notes?: string;
  };
  receiptNumber: string;
  branchInfo?: {
    name: string;
    address?: string;
    phone?: string;
    email?: string;
  };
}

const PaymentReceipt: React.FC<PaymentReceiptProps> = ({
  student,
  payment,
  receiptNumber,
  branchInfo,
}) => {
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-KE", {
      style: "currency",
      currency: "KES",
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatPaymentMethod = (method: string) => {
    return method.replace("_", " ").replace(/\b\w/g, (l) => l.toUpperCase());
  };

  return (
    <div
      className="bg-white p-8 max-w-md mx-auto"
      style={{ fontFamily: "Arial, sans-serif" }}
    >
      {/* Header */}
      <div className="text-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800 mb-2">
          {branchInfo?.name || "Educational Institution"}
        </h1>
        {branchInfo?.address && (
          <p className="text-sm text-gray-600">{branchInfo.address}</p>
        )}
        {branchInfo?.phone && (
          <p className="text-sm text-gray-600">Tel: {branchInfo.phone}</p>
        )}
        {branchInfo?.email && (
          <p className="text-sm text-gray-600">Email: {branchInfo.email}</p>
        )}
      </div>

      {/* Receipt Title */}
      <div className="text-center mb-6">
        <h2 className="text-xl font-semibold text-gray-800">PAYMENT RECEIPT</h2>
        <div className="mt-2">
          <p className="text-sm text-gray-600">
            Receipt No: <span className="font-medium">{receiptNumber}</span>
          </p>
          <p className="text-sm text-gray-600">
            Date:{" "}
            <span className="font-medium">
              {formatDate(payment.paymentDate)}
            </span>
          </p>
        </div>
      </div>

      {/* Student Information */}
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-3 border-b border-gray-300 pb-1">
          Student Information
        </h3>
        <div className="space-y-2">
          <div className="flex justify-between">
            <span className="text-gray-600">Name:</span>
            <span className="font-medium">
              {student.userInfo?.firstName} {student.userInfo?.lastName}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Student ADM:</span>
            <span className="font-medium">{student.admissionNumber}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Class:</span>
            <span className="font-medium">
              {student.currentClassId?.name ||
                student.currentClassId?.fullName ||
                "Not assigned"}
            </span>
          </div>
        </div>
      </div>

      {/* Payment Details */}
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-3 border-b border-gray-300 pb-1">
          Payment Details
        </h3>
        <div className="space-y-2">
          <div className="flex justify-between">
            <span className="text-gray-600">Amount Paid:</span>
            <span className="font-bold text-lg text-green-600">
              {formatCurrency(payment.amount)}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Payment Method:</span>
            <span className="font-medium">
              {formatPaymentMethod(payment.paymentMethod)}
            </span>
          </div>
          {payment.referenceNumber && (
            <div className="flex justify-between">
              <span className="text-gray-600">Reference No:</span>
              <span className="font-medium">{payment.referenceNumber}</span>
            </div>
          )}
          {payment.notes && (
            <div className="mt-3">
              <span className="text-gray-600">Notes:</span>
              <p className="font-medium mt-1">{payment.notes}</p>
            </div>
          )}
        </div>
      </div>

      {/* Fee Summary */}
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-3 border-b border-gray-300 pb-1">
          Fee Summary
        </h3>
        <div className="space-y-2">
          <div className="flex justify-between">
            <span className="text-gray-600">Total Fees:</span>
            <span className="font-medium">
              {formatCurrency(student.fees?.totalFeeStructure || 0)}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Total Paid:</span>
            <span className="font-medium text-green-600">
              {formatCurrency(student.fees?.totalPaid || 0)}
            </span>
          </div>
          <div className="flex justify-between border-t border-gray-300 pt-2">
            <span className="text-gray-600 font-semibold">
              Outstanding Balance:
            </span>
            <span className="font-bold text-red-600">
              {formatCurrency(student.fees?.totalBalance || 0)}
            </span>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="text-center mt-8 pt-4 border-t border-gray-300">
        <p className="text-xs text-gray-500">Thank you for your payment</p>
        <p className="text-xs text-gray-500 mt-1">
          This is a computer-generated receipt and does not require a signature
        </p>
      </div>

      {/* Print Instructions (hidden in print) */}
      <div className="text-center mt-4 print:hidden">
        <p className="text-xs text-gray-400">
          Please keep this receipt for your records
        </p>
      </div>
    </div>
  );
};

export default PaymentReceipt;
